import React, { FC, useState, useEffect } from 'react';
import { Popover } from '@mui/material';
import clsx from 'clsx';
import Button from '@/components/ui/Button';
import TextInput from '@/components/ui/TextInput';

interface IProps {
  amount: { min: number | null; max: number | null };
  onApply: (amount: { min: number | null; max: number | null }) => void;
}

const AmountFilter: FC<IProps> = ({ amount, onApply }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [tempAmount, setTempAmount] = useState(amount);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) {
      setTempAmount(amount);
    }
  }, [amount, open]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleApply = () => {
    onApply(tempAmount);
    handleClose();
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setTempAmount((prev) => ({ ...prev, min: value ? Number(value) : null }));
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setTempAmount((prev) => ({ ...prev, max: value ? Number(value) : null }));
  };

  return (
    <div>
      <div
        className="relative h-10 w-full"
        role="presentation"
        onClick={handleClick}
      >
        <input
          readOnly
          className={clsx(
            'w-full h-full cursor-pointer rounded border bg-white px-3 py-2 text-body2 placeholder:text-primary placeholder:font-bold',
            'border-grey-300 outline-primary',
            'focus:outline-1'
          )}
          placeholder="Filter by amount"
          value=""
        />
        <i
          className={clsx(
            'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-primary',
            open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
          )}
        />
      </div>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={open}
        PaperProps={{
          className:
            'shadow-[0px_4px_12px_-4px_rgba(64,64,64,0.20)] rounded-lg',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleClose}
      >
        <div className="w-64 p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-4">
            <TextInput
              label="Minimum"
              placeholder="Enter minimum amount"
              required={false}
              type="number"
              value={tempAmount.min?.toString() ?? ''}
              onChange={handleMinChange}
            />
            <TextInput
              label="Maximum"
              placeholder="Enter maximum amount"
              required={false}
              type="number"
              value={tempAmount.max?.toString() ?? ''}
              onChange={handleMaxChange}
            />
          </div>
          <Button
            className="w-full"
            label="Apply filter"
            size="small"
            variant="success"
            onClick={handleApply}
          />
        </div>
      </Popover>
    </div>
  );
};

export default AmountFilter;
